import React from 'react';
import { toast } from 'sonner';
import { ChevronDown, PartyPopper } from 'lucide-react';
import { adminApi } from '../../lib/adminAuth';
import { getTodayLocal } from '../../lib/dateUtils';

const dateLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', weekday: 'short' });

function Row({ f, onSaved, today }) {
  const [edit, setEdit] = React.useState(false);
  const [t, setT] = React.useState({ title: f.title, body: f.body });
  React.useEffect(() => setT({ title: f.title, body: f.body }), [f.title, f.body]);
  const past = f.date < today;
  const locked = f.status === 'sent' || past;
  const save = async (patch) => {
    try { await adminApi.put('/api/admin/push/festivals', { key: f.key, enabled: f.enabled, title: f.custom ? f.title : '', body: f.custom ? f.body : '', ...patch }); onSaved(); setEdit(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className={`p-3 rounded-xl border bg-white ${locked ? 'opacity-60' : ''}`} data-testid={`festival-${f.key}`}>
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-slate-500 w-24">{dateLabel(f.date)}</span>
        <span className="flex-1 text-sm font-medium text-slate-900 truncate">{f.name}</span>
        {f.status === 'sent' ? <span className="text-[11px] font-semibold text-emerald-700" data-testid={`festival-sent-${f.key}`}>Sent</span> : past ? <span className="text-[11px] text-slate-400">Passed</span> : (
          <button type="button" role="switch" aria-checked={f.enabled} onClick={() => save({ enabled: !f.enabled })} data-testid={`festival-toggle-${f.key}`}
            className={`relative w-9 h-5 rounded-full transition-colors ${f.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${f.enabled ? 'translate-x-4' : ''}`} />
          </button>
        )}
      </div>
      {edit ? (
        <div className="mt-2 space-y-2">
          <input value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid={`festival-title-${f.key}`} />
          <textarea value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid={`festival-body-${f.key}`} />
          <div className="flex justify-end gap-2">
            {f.custom && <button type="button" onClick={() => save({ title: '', body: '' })} className="h-8 px-3 text-xs text-slate-500" data-testid={`festival-reset-${f.key}`}>Reset to default</button>}
            <button type="button" onClick={() => setEdit(false)} className="h-8 px-3 text-xs text-slate-500">Cancel</button>
            <button type="button" onClick={() => save(t)} className="h-8 px-4 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold" data-testid={`festival-save-${f.key}`}>Save</button>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex items-start gap-2 pl-0 sm:pl-[108px]">
          <p className="flex-1 text-xs text-slate-500 truncate">{f.title} — {f.body}</p>
          <span className="text-[10px] text-slate-400 shrink-0">{f.custom ? 'Custom' : f.template ? `Template: ${f.template}` : 'General greeting'}</span>
          {!locked && <button type="button" onClick={() => setEdit(true)} className="text-[11px] font-semibold text-[#1a1a2e] hover:underline shrink-0" data-testid={`festival-edit-${f.key}`}>Edit</button>}
        </div>
      )}
    </div>
  );
}

export default function FestivalsPanel() {
  const [open, setOpen] = React.useState(true);
  const [showPast, setShowPast] = React.useState(false);
  const today = getTodayLocal();
  const [items, setItems] = React.useState([]);
  const load = React.useCallback(() => adminApi.get('/api/admin/push/festivals').then((r) => setItems(r.data)).catch(() => {}), []);
  React.useEffect(() => { load(); }, [load]);
  const upcoming = items.filter((f) => f.date >= today);
  const past = items.filter((f) => f.date < today);
  return (
    <div className="soft-card p-5" data-testid="festivals-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="festivals-toggle">
        <PartyPopper className="w-4 h-4 text-amber-500" />
        <span className="flex-1 font-semibold text-[#1a1a2e]">Festival greetings <span className="font-normal text-xs text-slate-500">sent automatically to everyone at 9 AM</span></span>
        <span className="text-xs text-slate-500" data-testid="festivals-on-count">{upcoming.filter((f) => f.enabled).length} on</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {!items.length && <p className="text-sm text-slate-400">No holidays in this year&apos;s calendar.</p>}
          {upcoming.map((f) => <Row key={f.key} f={f} onSaved={load} today={today} />)}
          {past.length > 0 && <button type="button" onClick={() => setShowPast(!showPast)} className="text-xs font-semibold text-slate-500 hover:underline" data-testid="festivals-show-past">{showPast ? 'Hide' : 'Show'} past days ({past.length})</button>}
          {showPast && past.map((f) => <Row key={f.key} f={f} onSaved={load} today={today} />)}
        </div>
      )}
    </div>
  );
}
